import React from 'react';
import { Button as MuiButton } from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled button component with custom design
const StyledButton = styled(MuiButton)(({ theme, variant, size }) => ({
  // Base styles for all buttons
  borderRadius: '8px',
  fontWeight: 500,
  textTransform: 'none',
  transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
  fontFamily: theme.typography.fontFamily,
  
  // Size variations
  ...(size === 'small' && {
    padding: '6px 12px',
    fontSize: '0.875rem',
    '& .MuiButton-startIcon': {
      marginRight: '6px',
      '& > svg': {
        fontSize: '18px',
      },
    },
    '& .MuiButton-endIcon': {
      marginLeft: '6px',
      '& > svg': {
        fontSize: '18px',
      },
    },
  }),
  ...(size === 'medium' && {
    padding: '8px 16px',
    fontSize: '0.9375rem',
    '& .MuiButton-startIcon': {
      marginRight: '8px',
      '& > svg': {
        fontSize: '20px',
      },
    },
    '& .MuiButton-endIcon': {
      marginLeft: '8px',
      '& > svg': {
        fontSize: '20px',
      },
    },
  }),
  ...(size === 'large' && {
    padding: '10px 20px',
    fontSize: '1rem',
    '& .MuiButton-startIcon': {
      marginRight: '10px',
      '& > svg': {
        fontSize: '22px',
      },
    },
    '& .MuiButton-endIcon': {
      marginLeft: '10px',
      '& > svg': {
        fontSize: '22px',
      },
    },
  }),
  
  // Default/Contained variant (solid purple)
  ...(variant === 'contained' && {
    backgroundColor: '#7C3AED',
    color: '#FFFFFF',
    border: 'none',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
    
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      color: '#FFFFFF',
    },
    
    '&:hover': {
      backgroundColor: '#6D28D9',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.12)',
      transform: 'translateY(-1px)',
    },
    
    '&:active': {
      backgroundColor: '#5B21B6',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)',
      transform: 'scale(0.98)',
    },
    
    '&:focus-visible': {
      outline: '2px solid #7C3AED',
      outlineOffset: '2px',
    },
    
    '&.Mui-disabled': {
      backgroundColor: '#E9D5FF',
      color: 'rgba(255, 255, 255, 0.6)',
      boxShadow: 'none',
    },
  }),
  
  // Outlined variant
  ...(variant === 'outlined' && {
    backgroundColor: '#FFFFFF',
    color: '#7C3AED',
    border: '2px solid #7C3AED',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      color: '#7C3AED',
    },
    
    '&:hover': {
      backgroundColor: '#F3E8FF',
      borderColor: '#6D28D9',
      color: '#6D28D9',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-1px)',
      
      '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        color: '#6D28D9',
      },
    },
    
    '&:active': {
      backgroundColor: '#EDE9FE',
      borderColor: '#5B21B6',
      color: '#5B21B6',
      transform: 'scale(0.98)',
      
      '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        color: '#5B21B6',
      },
    },
    
    '&:focus-visible': {
      outline: '2px solid #7C3AED',
      outlineOffset: '2px',
    },
    
    '&.Mui-disabled': {
      backgroundColor: '#F9FAFB',
      borderColor: '#E5E7EB',
      color: '#9CA3AF',
      
      '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        color: '#9CA3AF',
      },
    },
  }),
  
  // Text variant
  ...(variant === 'text' && {
    backgroundColor: 'transparent',
    color: '#7C3AED',
    
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      color: '#7C3AED',
    },
    
    '&:hover': {
      backgroundColor: 'rgba(124, 58, 237, 0.08)',
      color: '#6D28D9',
      
      '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        color: '#6D28D9',
      },
    },
    
    '&:active': {
      backgroundColor: 'rgba(124, 58, 237, 0.12)',
      color: '#5B21B6',
      
      '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        color: '#5B21B6',
      },
    },
    
    '&:focus-visible': {
      outline: '2px solid #7C3AED',
      outlineOffset: '2px',
    },
    
    '&.Mui-disabled': {
      color: '#9CA3AF',
      
      '& .MuiButton-startIcon, & .MuiButton-endIcon': {
        color: '#9CA3AF',
      },
    },
  }),
  
  // Ensure disabled state doesn't have pointer events
  '&.Mui-disabled': {
    cursor: 'not-allowed',
    pointerEvents: 'auto',
  },
}));

/**
 * AdminButton Component
 * 
 * A reusable button component designed for admin interfaces with consistent
 * styling across the application. Supports multiple variants and sizes.
 * 
 * @param {Object} props - Component props
 * @param {string} props.variant - Button variant: 'contained' | 'outlined' | 'text'
 * @param {string} props.size - Button size: 'small' | 'medium' | 'large'
 * @param {ReactNode} props.startIcon - Icon to display at the start of the button
 * @param {ReactNode} props.endIcon - Icon to display at the end of the button
 * @param {ReactNode} props.children - Button content
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {boolean} props.fullWidth - Whether the button should take full width
 * @param {function} props.onClick - Click handler
 * @param {string} props.color - Color variant (for compatibility, defaults to primary purple)
 * @param {Object} props.sx - Additional MUI sx styling
 * 
 * @example
 * // Basic usage
 * <AdminButton variant="contained">
 *   Save Changes
 * </AdminButton>
 * 
 * @example
 * // With icon
 * import { Add } from '@mui/icons-material';
 * <AdminButton variant="outlined" startIcon={<Add />}>
 *   Add Location
 * </AdminButton>
 * 
 * @example
 * // Small size, full width
 * <AdminButton size="small" fullWidth>
 *   Submit
 * </AdminButton>
 */
const AdminButton = React.forwardRef(({ 
  children,
  variant = 'contained',
  size = 'medium',
  color = 'primary',
  sx = {},
  ...props 
}, ref) => {
  return (
    <StyledButton
      ref={ref}
      variant={variant}
      size={size}
      sx={sx}
      {...props}
    >
      {children}
    </StyledButton>
  );
});

AdminButton.displayName = 'AdminButton';

export default AdminButton;